import React from 'react';
import { useRoleAccess } from '../hooks/useRoleAccess';
import { Card, Result, Button, Typography, Space, Tag } from 'antd';
import { LockOutlined, UserOutlined, SecurityScanOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { themeConfig } from '../config/theme';

const { Text, Paragraph } = Typography;

const RoleProtectedRoute = ({ 
  children, 
  requiredPermission, 
  pageName, 
  fallback = null,
  showAccessDenied = true 
}) => {
  const { canAccessPage, canPerformAction, userRole, loading, getRoleDescription, getRoleColor } = useRoleAccess();
  const navigate = useNavigate();

  if (loading) {
    return (
      <Card style={{ margin: 24, textAlign: 'center' }}>
        <div style={{ padding: '40px 0' }}>
          <SecurityScanOutlined style={{ fontSize: '48px', color: themeConfig.status.info, marginBottom: 16 }} />
          <div>Checking permissions...</div>
        </div>
      </Card>
    );
  }

  // Check if user can access the page
  const hasPageAccess = pageName ? canAccessPage(pageName) : true;
  
  // Check if user can perform the specific action
  const hasActionPermission = requiredPermission ? canPerformAction(requiredPermission) : true;

  // If user has access to both page and action, render children
  if (hasPageAccess && hasActionPermission) {
    return children;
  }

  // If fallback is provided, render it
  if (fallback) {
    return fallback;
  }

  // If showAccessDenied is false, render nothing
  if (!showAccessDenied) {
    return null;
  }

  // Special handling for IT users who can only access version management
  if (userRole === 'IT' && pageName !== 'version-management') {
    return (
      <Card style={{ margin: 24 }}>
        <Result
          status="403"
          icon={<LockOutlined style={{ color: themeConfig.status.error }} />}
          title="Access Denied"
          subTitle={
            <div>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <div>
                  <Text strong>Current Role: </Text>
                  <Tag color={getRoleColor(userRole)}>{userRole}</Tag>
                </div>
                <div>
                  <Text type="secondary">IT users can only access Version Management</Text>
                </div>
              </Space>
            </div>
          }
          extra={
            <Button 
              type="primary" 
              icon={<InfoCircleOutlined />}
              onClick={() => navigate('/version-management')}
            >
              Go to Version Management
            </Button>
          }
        />
      </Card>
    );
  }

  // Render access denied message
  return (
    <Card style={{ margin: 24 }}>
      <Result
        status="403"
        icon={<LockOutlined style={{ color: '#ff4d4f' }} />}
        title="Access Denied"
        subTitle={
          <div>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <div>
                <Text strong>Current Role: </Text>
                <Tag color={getRoleColor(userRole)}>{userRole}</Tag>
              </div>
              <div>
                <Text type="secondary">{getRoleDescription(userRole)}</Text>
              </div>
            </Space>
          </div>
        }
        extra={
          <Space direction="vertical" size="middle">
            <Paragraph type="secondary" style={{ textAlign: 'center', margin: 0 }}>
              {pageName && !hasPageAccess && (
                <div>
                  <Text strong>Page Access Required: </Text>
                  <Text code>{pageName}</Text>
                </div>
              )}
              {requiredPermission && !hasActionPermission && (
                <div>
                  <Text strong>Action Permission Required: </Text>
                  <Text code>{requiredPermission}</Text>
                </div>
              )}
            </Paragraph>
            
            <Space>
              <Button 
                type="primary" 
                icon={<UserOutlined />}
                onClick={() => navigate('/admin-management')}
              >
                Go to Admin Management
              </Button>
              <Button onClick={() => navigate('/dashboard')}>
                Go to Dashboard
              </Button>
            </Space>
          </Space>
        }
      />
    </Card>
  );
};

export default RoleProtectedRoute;
